import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as AiIcon } from '../../../assets/icons/trainings/ai.svg';
import AppAlert from '../../../components/designe/app-alert/AppAlert';
import AppExercisePropertyAvatar from '../../../components/designe/app-entity-avatar/app-exercise-property-avatar/AppExercisePropertyAvatar';
import AppButton from '../../../components/designe/buttons/app-button/AppButton';
import AppFormChipSelector from '../../../components/designe/forms/app-form-chip-selector';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import AppTimeSetterField from '../../../components/designe/wheels/app-time-setter-field/AppTimeSetterField';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import AppNavigate from '../../../components/reusable/app-navigate/AppNavigate';
import { useGetAppFeatures } from '../../../redux/api/appConfigApi';
import {
  AppFeatureKey,
  AvatarSize,
  AvatarTypes,
} from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { useStyles } from './GenerateTraining.styles';
import { useGenerateTraining } from './useGenerateTraining';

const PropertyChipAvatar = ({ tag }: { tag: IExerciseProperty }) => (
  <AppExercisePropertyAvatar
    exerciseProperty={tag}
    size={AvatarSize.xs}
    type={AvatarTypes.round}
  />
);

const GenerateTraining = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { data: features } = useGetAppFeatures();
  const isAiGenerationActive = features?.find(
    (f) => f.key === AppFeatureKey.aiGeneration,
  )?.isActive;

  if (!isAiGenerationActive) {
    return <AppNavigate to={Paths.trainings.toList} replace />;
  }

  const {
    formik,
    submitError,
    criteriaSubmitError,
    mainMuscleGroups,
    categories,
    equipments,
    developedSkillOptions,
    isLoading,
  } = useGenerateTraining();

  const getPropertyItem = useCallback((property: IExerciseProperty) => {
    return (
      <Box display="flex" gap={1} alignItems="center">
        <AppExercisePropertyAvatar
          exerciseProperty={property}
          size={AvatarSize.s}
          type={AvatarTypes.round}
        />
        <Typography variant="text-md">{property?.name}</Typography>
      </Box>
    );
  }, []);

  return (
    <PageContainer
      title={t('trainings.generateTraining')}
      description={t('trainings.generateTrainingDescription')}
      backButton>
      <Box className={classes.wrapper}>
        {submitError && <AppAlert severity="error">{submitError}</AppAlert>}
        <form onSubmit={formik.handleSubmit} className={classes.form}>
          <Box className={classes.selector}>
            <AppFormChipSelector
              options={mainMuscleGroups}
              AvatarComponent={PropertyChipAvatar}
              name="mainMuscleGroups"
              formik={formik}
              fieldName="name"
              label={t('trainings.generateMuscleGroups')}
              itemWrapperCallback={getPropertyItem}
              disabled={isLoading}
            />
          </Box>
          <Box className={classes.selector}>
            <AppFormChipSelector
              options={categories}
              AvatarComponent={PropertyChipAvatar}
              name="categories"
              formik={formik}
              fieldName="name"
              label={t('trainings.generateCategories')}
              itemWrapperCallback={getPropertyItem}
              disabled={isLoading}
            />
          </Box>
          <AppFormChipSelector
            options={equipments}
            AvatarComponent={PropertyChipAvatar}
            className={classes.selector}
            name="equipments"
            formik={formik}
            fieldName="name"
            label={t('trainings.generateEquipments')}
            itemWrapperCallback={getPropertyItem}
            disabled={isLoading}
          />
          <AppFormChipSelector
            options={developedSkillOptions}
            AvatarComponent={PropertyChipAvatar}
            className={classes.selector}
            name="developedSkills"
            formik={formik}
            fieldName="name"
            label={t('trainings.generateDevelopedSkills')}
            itemWrapperCallback={getPropertyItem}
            disabled={isLoading}
          />
          <Box className={classes.timeSetterWrapper}>
            <AppTimeSetterField
              value={formik.values.trainingDurationSeconds}
              onChange={(v) =>
                formik.setFieldValue('trainingDurationSeconds', v)
              }
              label={t('trainings.generateTrainingDuration')}
              fullWidth
              disabled={isLoading}
              textFieldVariant="default"
              pickerOptions={{
                showHours: true,
                showSeconds: false,
                maxHours: 10,
                minuteStep: 5,
              }}
            />
            <AppTimeSetterField
              value={formik.values.restDurationSeconds}
              onChange={(v) => formik.setFieldValue('restDurationSeconds', v)}
              label={t('trainings.generateRestDuration')}
              fullWidth
              disabled={isLoading}
              textFieldVariant="default"
              pickerOptions={{ maxMinutes: 10, secondStep: 5 }}
            />
          </Box>
          <AppFormTextField
            label={t('trainings.generatePromptLabel')}
            placeholder={t('trainings.generatePromptPlaceholder')}
            name="prompt"
            formik={formik}
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            disabled={isLoading}
            className={classes.input}
          />
          {criteriaSubmitError && (
            <AppAlert severity="error">{criteriaSubmitError}</AppAlert>
          )}
          <AppButton
            type="submit"
            loading={isLoading}
            disabled={isLoading || !formik.isValid}
            icon={<AiIcon />}>
            {t('trainings.generateSubmit')}
          </AppButton>
        </form>
      </Box>
    </PageContainer>
  );
};

export default GenerateTraining;
